import * as style from "./ApplicationList.module.scss";
import { Tabs, Tab, Box } from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ApplicationDetails from "./ApplicationDetails";
import Features from "./Features";
import ApplicationApis from "./ApplicationApis";
import CloneRoleModal from "./CloneRoleModal";

import RoleSetup from "../../Components/RoleSetup/RoleSetup";
import PanelComponent from "../../Components/PanelComponent/PanelComponent";
import DataDomainTable from "../../Components/DataDomain/DataDomainTable";
import CreateFeaturePanelContent from "./CreateFeaturePanelContent";
import EditIcon from "@mui/icons-material/Edit";
import {
  fetchApplicationById,
  setAppId,
  setApplicationEditMode,
} from "../../store/slices/applicationDetailsSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  editFeatureData,
  featureGroupListObject,
  saveFeatureData,
} from "../../store/slices/featureListSlice";
import { RootState } from "../../store/store";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CreateApiPanelContent from "./CreateApiPanelContent";
import { triggerToast } from "../../utils/toast";
import {
  setActivateButtonDisable,
  setCurrentRoleId,
  updateNamesData,
  updatecloneRoleData,
} from "../../store/slices/CreateRoleSlice";
import UserGroups from "../UserManagement/UserGroups/UserGroups";
import { BrowserStorage } from "../../utils/browserStorage";
import { sessionKey } from "../../Constants/appConstants";
import CustomBreadcrumb from "../../Components/CustomBreadcrumb/CustomBreadcrumb";
import PermissionManage from "../../Components/PermissionManage/PermissionManage";
import { resetPermissionData } from "../../store/slices/PermissionSlice/permissionSlice";
import ContextManage from "../../Components/ContextManage/ContextManage";
import PublishHistory from "../../Components/PublishHistory/PublishHistory";
import CubeAuthVersionList from "../../Components/CubeAuthVersionList/CubeAuthVersionList";
import { getActiveRoleDrodownData } from "../../services/RequestAccess/RequestAccess";
import Button from "../../Components/Common/CprimeButton";
import { base64PrivateKey, getEnv } from "../../Constants/env";
import { useIdToken } from "../../hooks/useAuthSession";
import axios from "axios";
import { decodePolicyData } from "../../utils/commonUtil";

const NON_EDITABLE_TABS = new Set([1, 2, 3, 4, 7, 10]);
const NON_EDITABLE_INFO_TABS = new Set([1, 2, 3, 4, 6, 7, 10]);

const tabSx = (selectedTab: number, value: number, width: string) => ({
  textTransform: "none" as const,
  fontWeight: selectedTab === value ? "bold" : "",
  width,
});

interface RoleOption {
  value: string;
  label: string | number;
}

const getCommonButtonTitle = (selectedTab: number): string => {
  const titleMap: Record<number, string> = {
    0: "Edit",
    1: "Create Feature",
    2: "Create API",
    3: "Create Role",
    4: "Add Permission",
    5: "Create Group",
    7: "Create Context",
    10: "Create Domain",
    11: "Create Version",
  };
  return titleMap[selectedTab] ?? "";
};

const getAdminStatus = (): boolean => {
  const authObject = BrowserStorage.getSessionItem(sessionKey) ?? "";
  const authJson = JSON.parse(authObject);
  return (
    authJson?.account?.idTokenClaims?.roles?.some(
      (role: string) => role.includes("RoleAdmin") || role.includes("AppAdmin"),
    ) ?? false
  );
};

const handleCreateButtonClick = (
  commonButtonTitle: string,
  setCreateGroupModal: (value: boolean) => void,
  setIsCreateContext: (value: boolean) => void,
  setIsCreateDataDomain: (value: boolean) => void,
  setIsCreateVersion: (value: boolean) => void,
  setOpenPanel: (value: boolean) => void,
  appId: number,
  navigate: any,
  dispatch: any,
) => {
  if (commonButtonTitle === "Create Group") {
    setCreateGroupModal(true);
  } else if (commonButtonTitle === "Create Context") {
    setIsCreateContext(true);
  } else if (commonButtonTitle === "Create Domain") {
    setIsCreateDataDomain(true);
  } else if (commonButtonTitle === "Create Version") {
    setIsCreateVersion(true);
  } else if (commonButtonTitle === "Create Role") {
    dispatch(updateNamesData(null));
    const payload = {
      isClone: false,
      is_active: false,
      sourceRoleId: null,
    };
    dispatch(updatecloneRoleData(payload as any));
    dispatch(setActivateButtonDisable(true));
    dispatch(setCurrentRoleId(null));
    navigate(`/application-list/app-${appId}/role/create-role`);
  } else {
    dispatch(updateNamesData(null));
    setOpenPanel(true);
  }
};

const renderPublishState = (rest: any): string => {
  if (!rest?.data?.publish_state) return "";

  const {
    publish_state,
    app_cmdb_id,
    last_published_version,
    last_changes_made_at,
  } = rest.data;

  if (publish_state === "PUBLISHED") {
    return `Published: ${app_cmdb_id}_${last_published_version} | Unpublished updates on: -`;
  } else if (publish_state === "UNPUBLISHED") {
    return `Published: ${app_cmdb_id}_${last_published_version} | Unpublished updates on: ${last_changes_made_at?.split("T")[0]}`;
  }
  return `Published: - | Unpublished updates on: ${last_changes_made_at?.split("T")[0]}`;
};

function ApplicationList() {
  const scssClass = style as any;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [openPanel, setOpenPanel] = useState(false);
  const [featureEdit, setFeatureEdit] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [createGroupModal, setCreateGroupModal] = useState(false);
  const [isCreateContext, setIsCreateContext] = useState(false);
  const [apiEdit, setApiEdit] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [isCreateDataDomain, setIsCreateDataDomain] = useState(false);
  const [isCreateVersion, setIsCreateVersion] = useState(false);
  const [isAccessible, setIsAccessible] = useState(false);
  const [showCubeAuthVersionTab, setShowCubeAuthVersionTab] = useState(false);
  const [canEditVersion, setCanEditVersion] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);
  const [featureGrpDropdown, setFeatureGrpDropdown] = useState<
    featureGroupListObject[]
  >([]);
  const [selectedTab, setSelectedTab] = useState(
    location?.state?.selectedTab ?? 0,
  );

  const { editMode, ...rest } = useSelector(
    (state: RootState) => state?.applicationDetails,
  );
  const data = rest.data;
  const idToken = useIdToken();

  const appId = useMemo(() => {
    return Number(id?.split("-")?.[1]);
  }, [id]);

  const commonButtonTitle = useMemo(
    () => getCommonButtonTitle(selectedTab),
    [selectedTab],
  );
  const isAdminOrSuperAdmin = useMemo(() => getAdminStatus(), []);

  // const envtype = getEnv()?.envtype;

  // const isEditable = useMemo(
  //   () => envtype !== "prod" && envtype !== "qa",
  //   [envtype],
  // );

  const isEditable = true; // As per new requirement, all environments are editable but changes in prod and qa should be made in lower env and promoted. So showing warning message for prod and qa env in UI.
  const checkIsAccessible = useCallback(async () => {
    if (!idToken)
      return {
        accessible: false,
        cubeAuthVersion: false,
        cubeAuthVersionEdit: false,
      };
    try {
      const { data, status } = await axios.get("/v1/roleconsumption/policy", {
        baseURL: getEnv()?.baseURL,
        headers: {
          Authorization: idToken,
        },
      });
      if (status !== 200)
        return {
          accessible: false,
          cubeAuthVersion: false,
          cubeAuthVersionEdit: false,
        };

      const decodeData = decodePolicyData(data?.data, base64PrivateKey);

      // QA API returns an array: [{ version, userId, Access: [...] }]
      // DEV API returns an object: { Access: [...] }
      // Handle both shapes by flattening all Access arrays.
      const accessList = Array.isArray(decodeData)
        ? decodeData.flatMap((item: any) => item?.Access ?? [])
        : (decodeData?.Access ?? []);

      const accessible = accessList.some((access: any) =>
        (access?.feature ?? []).some(
          (feature: any) =>
            feature?.FeatureName === "App Management" &&
            feature?.FeaturePermission?.some(
              (permission: any) => permission?.Permission === "Edit",
            ),
        ),
      );

      const cubeAuthVersion = accessList.some((access: any) =>
        (access?.feature ?? []).some(
          (feature: any) =>
            feature?.FeatureName === "CubeAuth Version Management" &&
            feature?.FeaturePermission?.some(
              (permission: any) => permission?.Permission === "View",
            ),
        ),
      );

      const cubeAuthVersionEdit = accessList.some((access: any) =>
        (access?.feature ?? []).some(
          (feature: any) =>
            feature?.FeatureName === "CubeAuth Version Management" &&
            feature?.FeaturePermission?.some(
              (permission: any) => permission?.Permission === "Edit",
            ),
        ),
      );

      return { accessible, cubeAuthVersion, cubeAuthVersionEdit };
    } catch (error) {
      // console.error("Error checking access:", error);
      return {
        accessible: false,
        cubeAuthVersion: false,
        cubeAuthVersionEdit: false,
      };
    }
  }, [idToken]);

  useEffect(() => {
    let isMounted = true;

    const loadAccess = async () => {
      const { accessible, cubeAuthVersion, cubeAuthVersionEdit } =
        await checkIsAccessible();
      if (isMounted) {
        setIsAccessible(accessible);
        setShowCubeAuthVersionTab(cubeAuthVersion);
        setCanEditVersion(cubeAuthVersionEdit);
      }
    };

    loadAccess();

    return () => {
      isMounted = false;
    };
  }, [checkIsAccessible]);

  const handleClose = useCallback(() => {
    setOpenPanel(false);
    setFeatureEdit(false);
    setApiEdit(false);
    dispatch(saveFeatureData());
    dispatch(editFeatureData());
  }, [dispatch]);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      if (editMode) {
        triggerToast(
          "Please save your changes before leaving the tab.",
          "error",
        );
      } else {
        setSelectedTab(newValue);
      }
    },
    [editMode],
  );

  const getDropdownDataById = useCallback(async () => {
    const roleAppId = id?.split("-")[1];
    try {
      const response = await getActiveRoleDrodownData(roleAppId);
      const responseData = response?.data;
      if (!responseData || typeof responseData !== "object") {
        return;
      }
      const formattedRoles = responseData.map(
        (role: { roleName: any; id: any }) => ({
          label: role?.roleName,
          value: role?.id,
        }),
      );
      setRoleList(formattedRoles);
    } catch (error) {
      // console.info("Error fetching dropdown data", error);
    }
  }, [id]);

  const handleOpenConfirmation = useCallback(() => {
    getDropdownDataById();
    setOpenDeleteModal(true);
  }, [getDropdownDataById]);

  const onCloseCloneRole = useCallback(() => {
    setOpenDeleteModal(false);
    setRoleList([]);
    setSelectedRoles([]);
  }, []);

  const cloneRoleHandler = useCallback(() => {
    const payload = {
      isClone: true,
      is_active: false,
      sourceRoleId: selectedRoles[0]?.value,
    };
    dispatch(updateNamesData(null));
    dispatch(updatecloneRoleData(payload as any));
    dispatch(setCurrentRoleId(null));
    dispatch(setActivateButtonDisable(true));
    navigate(`/application-list/app-${appId}/role/create-role`, {
      state: { cloneRoleId: selectedRoles, action: "clone_role" },
    });
  }, [dispatch, navigate, appId, selectedRoles]);

  const getApplicationDetails = useCallback(() => {
    dispatch(fetchApplicationById(Number(appId)) as any);
  }, [dispatch, appId]);

  useEffect(() => {
    if (idToken) {
      getApplicationDetails();
    }
    dispatch(setAppId(Number(appId)));
  }, [appId, dispatch, idToken]);

  useEffect(() => {
    dispatch(setApplicationEditMode(false));
  }, [dispatch]);

  return (
    <div className={scssClass.page}>
      <div
        className={scssClass.backButton}
        style={{ width: "100%", marginBottom: "1rem" }}
      >
        <CustomBreadcrumb
          items={[
            {
              label: "Application List",
              href: "/application-list",
              icon: <HomeOutlinedIcon sx={{ color: "#2469FF" }} />,
            },
            {
              label: `${data?.app_cmdb_name || ""}`,
              href: undefined,
            },
          ]}
        />
      </div>
      <div className={scssClass.appDetailContainer}>
        <div className={scssClass.header}>
          <Box>
            <Box>
              <span className={scssClass.title}>{data?.app_cmdb_name}</span>
              {rest?.data?.publish_state && (
                <span className={scssClass.publishState}>
                  {renderPublishState(rest)}
                </span>
              )}
            </Box>
            {/* {!isEditable && NON_EDITABLE_INFO_TABS.has(selectedTab) && ( 
               <Box marginTop={1} maxWidth="calc(100vw - 348px) ">
                 This section is not editable in {env} environment. Changes
                 should only be made in lower environments and promoted.
               </Box>
             )} */}
          </Box>
          {selectedTab === 0 && commonButtonTitle === "Edit" && !editMode ? (
            <Button
              variant="secondary"
              size="medium"
              sx={{
                padding: "0.75rem 1rem",
                height: "2.75rem",
                ".MuiSvgIcon-root": {
                  fontSize: "1rem !important",
                },
              }}
              onClick={() => dispatch(setApplicationEditMode(true))}
              disabled={editMode}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                {commonButtonTitle}
                <EditIcon
                  sx={{
                    height: "1rem",
                    width: "1rem",
                    marginLeft: "0.5rem",
                  }}
                />
              </span>
            </Button>
          ) : (
            <div style={{ margin: "0", padding: "3px" }}>
              {selectedTab === 3 && (
                <Button
                  variant="secondary"
                  sx={{
                    display: commonButtonTitle ? "" : "none",
                    marginRight: "8px",
                  }}
                  size="medium"
                  onClick={handleOpenConfirmation}
                  disabled={!isEditable || !isAccessible}
                >
                  Clone Role
                </Button>
              )}
              <Button
                variant="secondary"
                sx={{ display: commonButtonTitle ? "" : "none" }}
                size="medium"
                disabled={
                  (!isEditable && NON_EDITABLE_TABS.has(selectedTab)) ||
                  !isAccessible ||
                  (selectedTab === 11 && !canEditVersion)
                }
                onClick={() => {
                  handleCreateButtonClick(
                    commonButtonTitle,
                    setCreateGroupModal,
                    setIsCreateContext,
                    setIsCreateDataDomain,
                    setIsCreateVersion,
                    setOpenPanel,
                    appId,
                    navigate,
                    dispatch,
                  );
                }}
              >
                {commonButtonTitle}
              </Button>
            </div>
          )}
          {openDeleteModal && (
            // <Suspense fallback={<></>}>
            <CloneRoleModal
              open
              onClose={onCloseCloneRole}
              roleList={roleList}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
              onConfirm={cloneRoleHandler}
            />
            // </Suspense>
          )}
        </div>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="standard"
          aria-label="application tabs"
          sx={{
            zIndex: 1,
            // position: "sticky",
            // top: "3.5rem",
            backgroundColor: "white",
            width: "100%",
            "& .MuiButtonBase-root": {
              color: "black !important",
              backgroundColor: "white",
              padding: "1rem 2rem !important",
              minHeight: "50px",
            },
            "& .MuiTabs-indicator": {
              color: "black !important",
              backgroundColor: "black !important",
            },
            "& .MuiTabs-root": {
              marginBottom: "0px !important",
            },
            "& .Mui-disabled": {
              color: "grey !important",
            },
          }}
        >
          <Tab label="Details" value={0} sx={tabSx(selectedTab, 0, "111px")} />
          {isAdminOrSuperAdmin ? (
            <Tab label="Groups" value={5} sx={tabSx(selectedTab, 5, "114px")} />
          ) : null}
          <Tab
            label="Permissions"
            value={4}
            sx={tabSx(selectedTab, 4, "144px")}
          />
          <Tab label="Features" value={1} sx={tabSx(selectedTab, 1, "121px")} />
          <Tab label="APIs" value={2} sx={tabSx(selectedTab, 2, "93px")} />
          <Tab label="Domains" value={10} sx={tabSx(selectedTab, 10, "93px")} />
          <Tab label="Context" value={7} sx={tabSx(selectedTab, 7, "117px")} />
          <Tab label="Roles" value={3} sx={tabSx(selectedTab, 3, "101px")} />
          <Tab
            label="Publish & Promote"
            value={9}
            sx={tabSx(selectedTab, 9, "195px")}
          />
          {showCubeAuthVersionTab && (
            <Tab
              label="Cube Auth Version List"
              value={11}
              sx={tabSx(selectedTab, 11, "223px")}
            />
          )}
        </Tabs>
        <div className={scssClass.tabSeparator}></div>
        {selectedTab === 0 && (
          <ApplicationDetails setTabValue={setSelectedTab} />
        )}
        {selectedTab === 1 && (
          <Features
            setTabValue={(value: number) => setSelectedTab(Number(value))}
            setOpenEditPanel={setOpenPanel}
            setFeatureEdit={setFeatureEdit}
            setFeatureGrpDropdown={setFeatureGrpDropdown}
            appId={appId}
            isEditable={isEditable && isAccessible}
          />
        )}
        {selectedTab === 2 && (
          <ApplicationApis
            appId={appId}
            setApiEdit={setApiEdit}
            isEditable={isEditable && isAccessible}
          />
        )}
        {selectedTab === 3 && (
          <RoleSetup
            setTabValue={setSelectedTab}
            isEditable={isEditable && isAccessible}
          />
        )}
        {selectedTab === 4 && (
          <PermissionManage
            title="Permissions"
            openPanel={openPanel}
            setOpenPanel={(value: boolean) => {
              setOpenPanel(value);
              dispatch(resetPermissionData());
            }}
            getApplicationDetails={getApplicationDetails}
            isEditable={isEditable && isAccessible}
          />
        )}
        {isAdminOrSuperAdmin && selectedTab === 5 && (
          <UserGroups
            adminUserGroups={true}
            appId={appId}
            isAccessible={isAccessible}
            setCreateGroupModal={setCreateGroupModal}
            isCreateGroup={createGroupModal}
            getApplicationDetails={getApplicationDetails}
          />
        )}
        {selectedTab === 7 && (
          <ContextManage
            appId={appId}
            isCreateContext={isCreateContext}
            setIsCreateContext={setIsCreateContext}
            isEditable={isEditable && isAccessible}
          />
        )}
        {/* {selectedTab === 8 && (
          <ImportHistory appId={appId} appName={data?.app_cmdb_id} />
        )} */}
        {selectedTab === 9 && (
          <PublishHistory
            appId={appId}
            appName={data?.app_cmdb_id}
            isAccessible={isAccessible}
          />
        )}
        {selectedTab === 11 && (
          <CubeAuthVersionList
            appId={appId}
            appName={data?.app_cmdb_id}
            isAccessible={isAccessible}
            canEditVersion={canEditVersion}
            isCreateVersion={isCreateVersion}
            setIsCreateVersion={setIsCreateVersion}
          />
        )}
        {selectedTab === 10 && (
          <DataDomainTable
            appId={appId}
            isCreateDataDomain={isCreateDataDomain}
            getApplicationDetails={getApplicationDetails}
            setIsCreateDataDomain={setIsCreateDataDomain}
            isEditable={isEditable && isAccessible}
          />
        )}

        {openPanel && selectedTab === 1 && (
          <PanelComponent
            open={openPanel}
            title={featureEdit ? "Edit Feature" : "Create Feature"}
            isEdit={featureEdit}
            getApplicationDetails={getApplicationDetails}
            onClose={handleClose}
            BodyComponent={
              <CreateFeaturePanelContent
                appId={appId}
                isEdit={featureEdit}
                featureGrp={featureGrpDropdown}
                getApplicationDetails={getApplicationDetails}
              />
            }
            tabValue={selectedTab}
          />
        )}
        {openPanel && selectedTab === 2 && (
          <PanelComponent
            open={openPanel}
            title="Create API"
            isEdit={apiEdit}
            onClose={handleClose}
            getApplicationDetails={getApplicationDetails}
            BodyComponent={
              <CreateApiPanelContent
                appId={appId}
                isEdit={apiEdit}
                // setTabValue={setSelectedTab}
              />
            }
            tabValue={selectedTab}
          />
        )}
      </div>
    </div>
  );
}

export default ApplicationList;
